import { inject, Injectable } from '@angular/core';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import { map, switchMap, withLatestFrom } from 'rxjs/operators';
import {
  AccountDates,
  AccountsService,
  AddonActivation,
  AppAndAddonActivationStatus,
  ListAppsAndAddonsActivationsStatusesForBusinessResponseAppsAndAddonsActivationStatusesInterface as ActivationStatusesInterface,
} from '@vendasta/accounts/legacy';
import { Account as AccountProductDetails } from '@vendasta/accounts/legacy/_internal/objects/api';
import { App, AppKey, GetMultiAppRequest, PartnerApiService } from '@vendasta/marketplace-apps';
import { Activation } from './type';
import { InventoryListItem } from '../common/item-list/type';

@Injectable()
export class ActiveItemsService {
  private accountsService = inject(AccountsService);
  private partnerMarketplaceService = inject(PartnerApiService);

  getCurrentlyActiveItems(partnerId: string, businessId: string, marketId: string): Observable<InventoryListItem[]> {
    const appStatuses$ = this.listAppStatuses(businessId);

    const appKeys$ = appStatuses$.pipe(
      map((appStatuses) =>
        appStatuses.map(
          (appStatus) =>
            new AppKey({
              appId: appStatus.appId,
              editionId: appStatus.editionId,
            }),
        ),
      ),
    );

    const items$ = appKeys$.pipe(
      switchMap((appKeys) =>
        this.partnerMarketplaceService.getMultiApp(
          new GetMultiAppRequest({
            partnerId: partnerId,
            marketId: marketId,
            appKeys: appKeys,
            includeNotEnabled: true,
            projectionFilter: {
              paths: ['editionInformation', 'sharedMarketingInformation', 'trialConfiguration'],
            },
          }),
        ),
      ),
    );

    const activations$ = this.listActivationsForBusiness(businessId, partnerId);

    return combineLatest([items$, activations$]).pipe(
      withLatestFrom(appStatuses$),
      map(([[resp, activations], appStatuses]) => {
        const apps = resp?.apps ?? [];
        const appStatusMap = this.mapify<ActivationStatusesInterface>(
          appStatuses,
          (as: ActivationStatusesInterface) => as.appId,
        );
        const activationsMap = this.mapify<Activation>(activations, (ac: Activation) => ac.addonId || ac.appId);
        return apps.map((app: App) =>
          this.convertToInventoryListItem(app, appStatusMap.get(app.key.appId), activationsMap.get(app.key.appId)),
        );
      }),
    );
  }

  listAppStatuses(businessId: string): Observable<ActivationStatusesInterface[]> {
    return this.accountsService.listAppsAndAddonsActivationStatusesForBusiness(businessId).pipe(
      map((appStatuses) => {
        if (!appStatuses) return [];
        return appStatuses.filter((appStatus) => this.checkIfActive(appStatus));
      }),
    );
  }

  listActivationsForBusiness(businessId: string, partnerId: string): Observable<Activation[]> {
    const accountDates$ = this.accountsService.listAccountDates(businessId, '', 1000);

    const appActivations$ = this.listActiveProducts(businessId, partnerId).pipe(
      map((acc) => this.convertAppActivationsToActivations(acc)),
    );

    const addonActivations$ = appActivations$.pipe(
      map((appActivations) =>
        appActivations.map((act) =>
          this.accountsService.listAddonActivations(businessId, act.appId).pipe(map((resp) => resp.activations || [])),
        ),
      ),
      switchMap((responses: Observable<AddonActivation[]>[]) => {
        if (responses.length === 0) return of([]);

        return forkJoin([...responses]).pipe(
          map((arrays) => {
            const addonActivations: AddonActivation[] = [];
            return addonActivations.concat(addonActivations, ...arrays);
          }),
        );
      }),
      map((addonActivations) => this.convertAddonActivationsToActivations(addonActivations)),
    );

    return combineLatest([appActivations$, addonActivations$, accountDates$]).pipe(
      map(([appActivations, addonActivations, accountDates]) => {
        return appActivations
          .concat(addonActivations)
          .map((activation) => this.mergeActivationsAndDates(activation, accountDates));
      }),
    );
  }

  listActiveProducts(businessId: string, partnerId: string): Observable<AccountProductDetails[]> {
    return this.accountsService.list(businessId, partnerId).pipe(map((listResponse) => listResponse.accounts));
  }

  private convertToInventoryListItem(
    app: App,
    appStatus: ActivationStatusesInterface | undefined,
    activation: Activation | undefined,
  ): InventoryListItem {
    const activeItem: InventoryListItem = { itemId: '', type: 'app', name: '', iconUrl: '', quantity: 0 };

    activeItem.itemId = app.key.appId;
    activeItem.name = app.sharedMarketingInformation.name;
    activeItem.iconUrl = app.sharedMarketingInformation.iconUrl;

    if (app.sharedMarketingInformation.editionName) {
      activeItem.edition = { editionId: app.key.editionId, name: app.sharedMarketingInformation.editionName };
    }

    if (appStatus) {
      activeItem.quantity = appStatus.count ?? 0;

      if (appStatus.status === AppAndAddonActivationStatus.PENDING) {
        activeItem.status = 'Pending';
      }
      if (appStatus.status === AppAndAddonActivationStatus.CANCELED) {
        activeItem.status = 'Canceled';
      }
    }

    if (activation) {
      const isTrial = activation.trial;
      if (activation.deactivation) {
        const currentTime = new Date();
        const trialStatusIsActive = activation.deactivation > currentTime;
        if (isTrial) {
          activeItem.status = trialStatusIsActive ? 'Demo' : 'Demo expired';
        }
      }
      activeItem.descriptor = activation.activationDescriptor;
    }

    return activeItem;
  }

  private convertAppActivationsToActivations(accounts: AccountProductDetails[]): Activation[] {
    if (!accounts) return [];
    return accounts.map((acc) => ({ ...acc, appId: this.getAppId(acc) }));
  }

  private convertAddonActivationsToActivations(activations: AddonActivation[]): Activation[] {
    if (!activations) return [];

    const now = new Date();
    return activations
      .filter((activation) => !activation.deactivated || activation.deactivated > now)
      .map((a) => ({ ...a, activation: a.activated, deactivation: a.deactivated }));
  }

  private getAppId(account: AccountProductDetails): string {
    if (account.productId === 'MP') {
      return account.appId;
    } else if (account.productId === 'CP') {
      return account.accountId;
    } else {
      return account.productId;
    }
  }

  private checkIfActive(appStatus: ActivationStatusesInterface): boolean {
    const isActivated = appStatus.status === AppAndAddonActivationStatus.ACTIVATED;
    const isCancelled = appStatus.status === AppAndAddonActivationStatus.CANCELED;
    const isPending = appStatus.status === AppAndAddonActivationStatus.PENDING;
    return isActivated || isCancelled || isPending;
  }

  private mergeActivationsAndDates(activation: Activation, accountDates: AccountDates[]): Activation {
    if (!accountDates || accountDates.length < 1) return activation;

    const date =
      accountDates.find((item) => item.activationId === activation.activationId) ||
      accountDates.find((item) => item.appId === activation.appId && item.addonId === activation.addonId);
    if (date) {
      if (!activation.deactivation || activation.deactivation.getFullYear() <= 1900) {
        activation.deactivation = date.pendingDeactivation;
      }
    }
    return activation;
  }

  private mapify<T>(array: T[], keyFn: (item: T) => string | undefined): Map<string, T> {
    return array.reduce((map, item) => {
      const key = keyFn(item);
      if (key !== undefined) {
        map.set(key, item);
      }
      return map;
    }, new Map<string, T>());
  }
}
